class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.updateCartUpsellToggleState();
        cartDrawerItems.updateCartUpsellVisibility();
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    const scrollY = window.scrollY;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /**
   * Override updateQuantity for the drawer to do TARGETED DOM updates
   * instead of replacing the entire .drawer__inner innerHTML.
   * This prevents: timer resets, price flashes, loading jank.
   */
  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Parse the new HTML from Shopify's section rendering API
        const newDoc = new DOMParser().parseFromString(
          parsedState.sections['cart-drawer'],
          'text/html'
        );

        // --- TARGETED UPDATES instead of full innerHTML replace ---

        // 1) Update cart icon bubble (small element, full replace is fine)
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && parsedState.sections['cart-icon-bubble']) {
          const newIconDoc = new DOMParser().parseFromString(
            parsedState.sections['cart-icon-bubble'],
            'text/html'
          );
          const newIconContent = newIconDoc.querySelector('.shopify-section');
          if (newIconContent) {
            cartIconBubble.innerHTML = newIconContent.innerHTML;
          }
        }

        // 2) If cart became empty or items were added/removed, do a full replace
        //    (structural change = full replace is necessary)
        const currentItemCount = document.querySelectorAll('#CartDrawer-CartItems .cart-item').length;
        const newItemCount = parsedState.items.length;

        if (parsedState.item_count === 0 || currentItemCount !== newItemCount) {
          // Structural change — full replace needed
          this._fullReplace(newDoc, parsedState, name, line, cartDrawerWrapper);
        } else {
          // 3) Same number of items — do targeted updates (the common case: qty +/-)
          this._targetedUpdate(newDoc, parsedState, line);
        }

        // 4) Update the header title (item count)
        const newHeading = newDoc.querySelector('.drawer__heading');
        const currentHeading = document.querySelector('.drawer__heading');
        if (newHeading && currentHeading) {
          currentHeading.innerHTML = newHeading.innerHTML;
        }

        // 5) Update footer (totals, savings, checkout button price)
        this._updateFooter(newDoc);

        // 6) Handle focus
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement?.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        // 7) Update progress bar
        this.updateProgressBar({
          total_price: parsedState.total_price,
          items_subtotal_price: parsedState.items_subtotal_price,
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        this.updateCartUpsellToggleState();
        this.updateCartUpsellVisibility(parsedState.item_count);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /**
   * Targeted update: only update quantity inputs, prices, and line totals
   * for each cart item — leave everything else (timer, progress bar, images, structure) untouched.
   */
  _targetedUpdate(newDoc, parsedState, activeLine) {
    parsedState.items.forEach((item, index) => {
      const lineIndex = index + 1;
      const currentRow = document.getElementById(`CartDrawer-Item-${lineIndex}`);
      const newRow = newDoc.querySelector(`#CartDrawer-Item-${lineIndex}`);

      if (!currentRow || !newRow) return;

      // Update quantity input value (without replacing the element)
      const currentQtyInput = currentRow.querySelector('.quantity__input');
      const newQtyInput = newRow.querySelector('.quantity__input');
      if (currentQtyInput && newQtyInput) {
        currentQtyInput.value = newQtyInput.value;
        currentQtyInput.setAttribute('value', newQtyInput.getAttribute('value'));
        // Update data-cart-quantity attribute too
        if (newQtyInput.hasAttribute('data-cart-quantity')) {
          currentQtyInput.setAttribute('data-cart-quantity', newQtyInput.getAttribute('data-cart-quantity'));
        }
      }

      // Update line item price (the total for this line)
      const currentPriceWrapper = currentRow.querySelector('.cart-item__price-wrapper');
      const newPriceWrapper = newRow.querySelector('.cart-item__price-wrapper');
      if (currentPriceWrapper && newPriceWrapper) {
        // Use a smooth transition for the price update
        currentPriceWrapper.style.transition = 'opacity 0.15s ease';
        currentPriceWrapper.style.opacity = '0.4';
        requestAnimationFrame(() => {
          currentPriceWrapper.innerHTML = newPriceWrapper.innerHTML;
          requestAnimationFrame(() => {
            currentPriceWrapper.style.opacity = '1';
          });
        });
      }

      // Update unit price area in details (if discount prices changed)
      const currentDetails = currentRow.querySelector('.cart-item__details .cart-item__discounted-prices');
      const newDetails = newRow.querySelector('.cart-item__details .cart-item__discounted-prices');
      if (currentDetails && newDetails) {
        currentDetails.innerHTML = newDetails.innerHTML;
      }

      // Update error messages
      const currentError = document.getElementById(`CartDrawer-LineItemError-${lineIndex}`);
      const newError = newDoc.querySelector(`#CartDrawer-LineItemError-${lineIndex}`);
      if (currentError && newError) {
        currentError.innerHTML = newError.innerHTML;
      }
    });
  }

  /**
   * Update footer elements: totals, tax note, discounts, checkout button price.
   * Only replaces the specific parts, not the entire footer.
   */
  _updateFooter(newDoc) {
    // Update totals value
    const currentTotalValue = document.querySelector('.cart-drawer__footer .totals__total-value');
    const newTotalValue = newDoc.querySelector('.cart-drawer__footer .totals__total-value');
    if (currentTotalValue && newTotalValue) {
      currentTotalValue.style.transition = 'opacity 0.15s ease';
      currentTotalValue.style.opacity = '0.4';
      requestAnimationFrame(() => {
        currentTotalValue.textContent = newTotalValue.textContent;
        requestAnimationFrame(() => {
          currentTotalValue.style.opacity = '1';
        });
      });
    }

    // Update checkout button price
    const currentCheckoutBtn = document.getElementById('CartDrawer-Checkout');
    const newCheckoutBtn = newDoc.querySelector('#CartDrawer-Checkout');
    if (currentCheckoutBtn && newCheckoutBtn) {
      currentCheckoutBtn.innerHTML = newCheckoutBtn.innerHTML;
    }

    // Update discounts list
    const currentDiscounts = document.querySelector('.cart-drawer__footer .discounts');
    const newDiscountsParent = newDoc.querySelector('.cart-drawer__footer .discounts')?.parentElement;
    const currentDiscountsParent = currentDiscounts?.parentElement;
    if (currentDiscountsParent) {
      const newDiscounts = newDoc.querySelector('.cart-drawer__footer .discounts');
      if (newDiscounts) {
        if (currentDiscounts) {
          currentDiscounts.innerHTML = newDiscounts.innerHTML;
        } else {
          currentDiscountsParent.innerHTML = newDiscountsParent.innerHTML;
        }
      } else if (currentDiscounts) {
        currentDiscounts.remove();
      }
    }

    // Update savings bar (custom "Vous économisez" message)
    const currentSavings = document.getElementById('custom-cart-savings-bar');
    const newSavings = newDoc.querySelector('#custom-cart-savings-bar');
    if (currentSavings && newSavings) {
      currentSavings.innerHTML = newSavings.innerHTML;
      currentSavings.style.display = newSavings.style.display;
    }
  }

  /**
   * Full replace fallback — used when items are added/removed (structural change).
   * Still preserves elements outside .drawer__contents where possible.
   */
  _fullReplace(newDoc, parsedState, name, line, cartDrawerWrapper) {
    // Replace cart items area
    const currentCartItems = document.getElementById('CartDrawer-CartItems');
    const newCartItems = newDoc.querySelector('#CartDrawer-CartItems');
    if (currentCartItems && newCartItems) {
      currentCartItems.innerHTML = newCartItems.innerHTML;
    }

    // Replace the empty state if needed
    const currentInner = document.querySelector('#CartDrawer .drawer__inner');
    const newInner = newDoc.querySelector('.drawer__inner');
    if (currentInner && newInner) {
      // Replace empty content block
      const currentEmpty = currentInner.querySelector('.drawer__inner-empty');
      const newEmpty = newInner.querySelector('.drawer__inner-empty');
      if (newEmpty && !currentEmpty) {
        // Cart became empty — insert empty state
        currentInner.insertBefore(newEmpty.cloneNode(true), currentInner.firstChild);
      } else if (!newEmpty && currentEmpty) {
        // Cart was empty, now has items — remove empty state
        currentEmpty.remove();
      }
    }

    // Update the full footer since structure may have changed
    const currentFooter = document.querySelector('#CartDrawer .drawer__footer');
    const newFooter = newDoc.querySelector('.drawer__footer');
    if (currentFooter && newFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
    }

    // Re-bind overlay click
    setTimeout(() => {
      const overlay = document.getElementById('CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', () => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.close();
        });
      }
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
